import { fetcher } from '../../lib/api-fetcher';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AuctionDetail, BidDetail, SubscriberListItem } from './types.js';

export const auctionKeys = {
  all: ['auctions'] as const,
  detail: (id: string) => [...auctionKeys.all, 'detail', id] as const,
  subscribers: (schemeId: string) => ['schemes', schemeId, 'subscribers'] as const,
};

export function useAuction(auctionId: string | null) {
  return useQuery({
    queryKey: auctionKeys.detail(auctionId ?? ''),
    queryFn: async () => {
      const res = await fetcher.get<{ auction: AuctionDetail; bids: BidDetail[]; attendeeSubscriberIds: string[] }>(
        `/staff/v1/auctions/${auctionId}`
      );
      return res;
    },
    enabled: !!auctionId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 5000;
      const status = data.auction.status as string;
      if (status === 'CONFIRMED' || status === 'CANCELLED' || status === 'NO_WINNER') return false;
      if (status === 'BIDDING_CLOSED' || status === 'WINNER_DETERMINED') return 3000;
      return 5000;
    },
  });
}

export function useSchemeSubscribers(schemeId: string) {
  return useQuery({
    queryKey: auctionKeys.subscribers(schemeId),
    queryFn: async () => {
      const res = await fetcher.get<SubscriberListItem[] | { data: SubscriberListItem[] }>(
        `/staff/v1/chit-groups/${schemeId}/subscribers?page=1&limit=100`
      );
      return Array.isArray(res) ? res : res?.data ?? [];
    },
    enabled: !!schemeId,
  });
}

export function useOpenAuction(schemeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { cycleId: string; auctionType: string }) =>
      fetcher.post<{ auctionId: string }>('/staff/v1/auctions/open', {
        schemeId,
        ...body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schemes', 'detail', schemeId, 'cycles'] });
    },
  });
}

export function useMarkAttendance(auctionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { subscriberIds: string[] }) =>
      fetcher.post<{ success: boolean }>(`/staff/v1/auctions/${auctionId}/attendance`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: auctionKeys.detail(auctionId) });
    },
  });
}

export function useSubmitBid(auctionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { subscriptionId?: string; subscriberId?: string; bidDiscount: number }) =>
      fetcher.post<{ bidId: string }>(`/staff/v1/auctions/${auctionId}/bids`, {
        subscriptionId: body.subscriptionId || body.subscriberId,
        bidDiscount: body.bidDiscount,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: auctionKeys.detail(auctionId) });
    },
  });
}

export function useCloseBidding(auctionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => fetcher.post<{ success: boolean }>(`/staff/v1/auctions/${auctionId}/close-bidding`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: auctionKeys.detail(auctionId) });
    },
  });
}

export function useConfirmWinner(auctionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body?: { conductedBy?: string; docCharge?: number; taxAmount?: number; totalCharge?: number }) => fetcher.post<{ success: boolean }>(`/staff/v1/auctions/${auctionId}/confirm`, body || {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: auctionKeys.detail(auctionId) });
    },
  });
}

export function useRescheduleAuction(auctionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { scheduledAt?: string; scheduledCloseAt?: string; reason: string }) =>
      fetcher.post<{ success: boolean }>(`/staff/v1/auctions/${auctionId}/reschedule`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: auctionKeys.detail(auctionId) });
    },
  });
}

export function useCorrectAuctionResult(auctionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { reason: string }) =>
      fetcher.post<{ success: boolean; approvalRequestId?: string }>(`/staff/v1/auctions/${auctionId}/correct-result`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: auctionKeys.detail(auctionId) });
    },
  });
}

export function useCancelAuction(auctionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { reason: string }) =>
      fetcher.post<{ success: boolean }>(`/staff/v1/auctions/${auctionId}/cancel`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: auctionKeys.detail(auctionId) });
    },
  });
}

// Backend AuctionListDto fields (from auction-query.service.ts):
// id, schemeId, schemeName, cycleId, cycleNumber, auctionType, status,
// scheduledDate, winnerSubscriptionId, winningDiscount, netPrizeAmount, confirmedAt
// Status values: 'OPEN' | 'BIDDING_CLOSED' | 'WINNER_DETERMINED' | 'CONFIRMED' | 'CANCELLED'

export type RawAuctionDto = {
  id: string;
  schemeId: string;
  schemeName: string | null;
  cycleId: string;
  cycleNumber: number;
  auctionType: string;
  status: string;
  scheduledDate: string | null;
  winnerSubscriptionId: string | null;
  winningDiscount: number | null;
  netPrizeAmount: number | null;
  confirmedAt: string | null;
};

export type AuctionListItem = {
  id: string;
  schemeCode: string;       // mapped from schemeName
  auctionNo: number;        // mapped from cycleNumber
  date: string;             // mapped from scheduledDate
  status: 'active' | 'upcoming' | 'completed'; // mapped from backend status
  baseAmount: number;       // mapped from netPrizeAmount
  winner?: string | null;   // will be populated from winnerSubscriptionId (needs join or separate fetch)
  bidAmount?: number | null; // mapped from winningDiscount (as paise)
  rawStatus: string;        // original backend status for debugging
  rawNetPrizeAmount: number; // original netPrizeAmount
  rawWinningDiscount: number | null; // original winningDiscount
};

function mapAuctionStatus(raw: string): 'active' | 'upcoming' | 'completed' {
  switch (raw) {
    case 'OPEN':
    case 'BIDDING_CLOSED':
    case 'WINNER_DETERMINED':
      return 'active';
    case 'CONFIRMED':
      return 'completed';
    case 'CANCELLED':
    default:
      return 'upcoming'; // treat cancelled as upcoming (inactive)
  }
}

function mapAuctions(raw: RawAuctionDto[]): AuctionListItem[] {
  return raw.map((a) => ({
    id: a.id,
    schemeCode: a.schemeName ?? a.schemeId ?? '—',
    auctionNo: a.cycleNumber ?? 0,
    date: a.scheduledDate ?? '',
    status: mapAuctionStatus(a.status),
    baseAmount: a.netPrizeAmount ?? 0,
    winner: a.winnerSubscriptionId ? `Subscription ${a.winnerSubscriptionId}` : null,
    bidAmount: a.winningDiscount ?? 0,
    rawStatus: a.status,
    rawNetPrizeAmount: a.netPrizeAmount ?? 0,
    rawWinningDiscount: a.winningDiscount ?? null,
  }));
}

export function useAuctionsList(filters?: { status?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: [...auctionKeys.all, 'list', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.page) params.append('page', String(filters.page));
      if (filters?.limit) params.append('limit', String(filters.limit));
      const qs = params.toString();
      const response = await fetcher.get<{ data: RawAuctionDto[]; meta: Record<string, unknown> }>(
        `/staff/v1/auctions${qs ? `?${qs}` : ''}`
      );
      return {
        ...response,
        data: mapAuctions(response.data),
      };
    },
  });
}
